import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/admin/_common/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/admin/_common/ui/dropdown-menu";
import { Badge } from "@/app/admin/_common/ui/badge";
import CuoEmpty from "@/app/admin/_common/CuoEmpty";
import { formatUtcDate } from "@/app/_utils/dayjs";
import type { FormRequest } from "@/app/_services/contactService";
import { cn } from "@/app/_utils/cn";
import { STATUS_LABELS, FORM_TYPE_LABELS } from "./requestLabels";
import { Inbox, MoreHorizontal, Circle, Check, X, Mail, Phone, MessageSquare, Handshake } from "lucide-react";
import { useIsMobile } from "@/app/admin/_hooks/useIsMobile";

const STATUS_BADGE_CLASSES: Record<FormRequest["status"], string> = {
  new: "border-blue-200 bg-blue-50 text-blue-700",
  accepted: "border-emerald-200 bg-emerald-50 text-emerald-700",
  declined: "border-red-200 bg-red-50 text-red-600",
};

const STATUS_DOT_CLASSES: Record<FormRequest["status"], string> = {
  new: "bg-blue-500",
  accepted: "bg-emerald-500",
  declined: "bg-red-500",
};

const STATUS_ICON_CLASSES: Record<FormRequest["status"], string> = {
  new: "text-blue-500",
  accepted: "text-emerald-500",
  declined: "text-red-500",
};

const STATUS_OPTIONS: { status: FormRequest["status"]; icon: typeof Circle }[] = [
  { status: "new", icon: Circle },
  { status: "accepted", icon: Check },
  { status: "declined", icon: X },
];

const FORM_TYPE_ICONS: Record<FormRequest["form_type"], typeof MessageSquare> = {
  contact_us: MessageSquare,
  work_with_us: Handshake,
};

export default function RequestList({
  requests,
  onView,
  onUpdateStatus,
}: {
  requests: FormRequest[];
  onView: (request: FormRequest) => void;
  onUpdateStatus: (request: FormRequest, status: FormRequest["status"]) => void;
}) {
  const isMobile = useIsMobile();

  const statusMenu = (request: FormRequest) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-auto flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
        >
          <MoreHorizontal className="size-4 shrink-0" size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {STATUS_OPTIONS.map(({ status, icon: Icon }) => (
          <DropdownMenuItem
            key={status}
            disabled={request.status === status}
            onClick={() => onUpdateStatus(request, status)}
          >
            <Icon className={`mr-2 size-4 ${STATUS_ICON_CLASSES[status]}`} />
            Mark as {STATUS_LABELS[status]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col bg-card">
        {requests.length === 0 ? (
          <div className="flex min-h-[400px] flex-col">
            <CuoEmpty icon={Inbox} text="No requests" />
          </div>
        ) : (
          <div className="flex flex-col">
            {requests.map((request) => (
              <div
                key={request.id}
                className="flex cursor-pointer flex-col gap-1.5 border-b border-border px-4 py-3 last:border-0"
                onClick={() => onView(request)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-[14px] font-semibold text-foreground">
                    {request.name || "-"}
                  </span>
                  <div className="flex flex-none items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`gap-1.5 rounded-[7px] font-normal ${STATUS_BADGE_CLASSES[request.status]}`}
                    >
                      <span className={`size-1.5 flex-none rounded-full ${STATUS_DOT_CLASSES[request.status]}`} />
                      {STATUS_LABELS[request.status] ?? request.status}
                    </Badge>
                    <div onClick={(e) => e.stopPropagation()}>{statusMenu(request)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                  <Mail className="size-3.5 flex-none" />
                  {request.email ? (
                    <a
                      href={`mailto:${request.email}`}
                      className="truncate text-muted-foreground hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {request.email}
                    </a>
                  ) : (
                    "-"
                  )}
                </div>

                <div className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
                  <Phone className="size-3.5 flex-none" />
                  {request.phone_number || "-"}
                </div>

                <div className="flex items-center justify-between gap-2 text-[12px] text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    {(() => {
                      const FormTypeIcon = FORM_TYPE_ICONS[request.form_type];
                      return FormTypeIcon ? <FormTypeIcon className="size-3.5 flex-none" /> : null;
                    })()}
                    {FORM_TYPE_LABELS[request.form_type] ?? request.form_type}
                  </span>
                  <span>{formatUtcDate(request.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border bg-card md:rounded-md md:border md:border-border">
      <Table
        containerClassName="min-h-0 flex-1 overflow-x-auto overflow-y-auto"
        className={cn("min-w-[900px] table-fixed", requests.length === 0 && "h-full")}
      >
        <TableHeader>
          <TableRow className="sticky top-0 z-10 hover:bg-muted [&>th]:bg-muted">
            <TableHead className="h-8 w-[18%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Name
            </TableHead>
            <TableHead className="h-8 w-[20%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Email
            </TableHead>
            <TableHead className="h-8 w-[13%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Phone
            </TableHead>
            <TableHead className="h-8 w-[13%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Form Type
            </TableHead>
            <TableHead className="h-8 w-[10%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </TableHead>
            <TableHead className="h-8 w-[13%] text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Created
            </TableHead>
            <TableHead className="h-8 w-12 pr-6 text-right" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {requests.length === 0 ? (
            <TableRow className="h-full hover:bg-transparent">
              <TableCell colSpan={7} className="h-full p-0">
                <CuoEmpty icon={Inbox} text="No requests" />
              </TableCell>
            </TableRow>
          ) : (
            requests.map((request) => (
            <TableRow
              key={request.id}
              // Overrides TableCell's default p-4 once, rather than on all seven cells.
              className="cursor-pointer hover:bg-muted/50 [&>td]:py-1.5"
              onClick={() => onView(request)}
            >
              <TableCell className="truncate text-[12.5px] text-foreground">
                {request.name || "-"}
              </TableCell>
              <TableCell className="truncate text-[12.5px] text-muted-foreground">
                {request.email ? (
                  <a
                    href={`mailto:${request.email}`}
                    className="text-foreground hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {request.email}
                  </a>
                ) : (
                  "-"
                )}
              </TableCell>
              <TableCell className="whitespace-nowrap text-[12.5px] text-muted-foreground">
                {request.phone_number || "-"}
              </TableCell>
              <TableCell className="whitespace-nowrap text-[12.5px] text-muted-foreground">
                {FORM_TYPE_LABELS[request.form_type] ?? request.form_type}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                <Badge
                  variant="outline"
                  className={`gap-1.5 rounded-[7px] font-normal ${STATUS_BADGE_CLASSES[request.status]}`}
                >
                  <span className={`size-1.5 flex-none rounded-full ${STATUS_DOT_CLASSES[request.status]}`} />
                  {STATUS_LABELS[request.status] ?? request.status}
                </Badge>
              </TableCell>
              <TableCell className="whitespace-nowrap text-[12.5px] text-muted-foreground">
                {formatUtcDate(request.created_at)}
              </TableCell>
              <TableCell className="pr-6 text-right" onClick={(e) => e.stopPropagation()}>
                {statusMenu(request)}
              </TableCell>
            </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
